import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges
} from '@angular/core';
import { GameManager, gameManager } from 'src/app/game/businesslogic/GameManager';
import { GhsManager } from 'src/app/game/businesslogic/GhsManager';
import { SettingsManager, settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { Character } from 'src/app/game/model/Character';
import { AttackModifierDeck, AttackModifierType } from 'src/app/game/model/data/AttackModifier';
import { AttackModifierComponent } from 'src/app/ui/figures/attackmodifier/attackmodifier';
import { TrackUUIDPipe } from 'src/app/ui/helper/trackUUID';

@Component({
  imports: [AttackModifierComponent, NgClass, TrackUUIDPipe],
  selector: 'ghs-attackmodifier-draw',
  templateUrl: './attackmodifier-draw.html',
  styleUrls: ['./attackmodifier-draw.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttackModifierDrawComponent implements OnInit, OnChanges {
  element = inject(ElementRef);
  private ghsManager = inject(GhsManager);

  private cdr = inject(ChangeDetectorRef);

  readonly inputCharacter = input.required<Character>({ alias: 'character' });
  get character(): Character {
    return this.inputCharacter();
  }

  readonly drawingEmitter = output<boolean>({ alias: 'drawing' });
  readonly initTimeout = input<number>(1500);

  gameManager: GameManager = gameManager;
  settingsManager: SettingsManager = settingsManager;

  deck!: AttackModifierDeck;
  numeration: string = '';
  characterIcon: string = '';

  AttackModifierType = AttackModifierType;
  current: number = -1;
  drawing: boolean = false;
  drawTimeout: any = null;
  queue: number = 0;
  queueTimeout: any = null;
  newStyle: boolean = false;

  constructor() {
    this.ghsManager.uiChangeEffect(() => this.update());
    this.element.nativeElement.addEventListener('pointerdown', (event: any) => {
      const elements = document.elementsFromPoint(event.clientX, event.clientY);
      if (elements[0].classList.contains('attack-modifiers') && elements.length > 2) {
        (elements[2] as HTMLElement).click();
      }
    });
  }

  ngOnInit(): void {
    this.deck = this.character.attackModifierDeck;
    this.characterIcon = this.character.iconUrl;
    this.numeration = '' + this.character.number;
    this.newStyle = gameManager.newAmStyle(this.character.edition);

    if (settingsManager.settings.fhStyle) {
      this.newStyle = true;
    }

    this.current = this.deck.current;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deck']) {
      this.update();
    }
  }

  update() {
    if (this.current < this.deck.current) {
      this.queue = Math.max(0, this.deck.current - this.current);
      if (!this.queueTimeout) {
        this.queue--;
        this.current++;
        this.drawQueue();
      }
    } else if (!this.queueTimeout || this.deck.current < this.current + this.queue) {
      if (this.queueTimeout) {
        clearTimeout(this.queueTimeout);
        this.queueTimeout = null;
      }
      this.queue = 0;
      this.drawing = false;
      this.drawingEmitter.emit(false);
      this.current = this.deck.current;
    }
  }

  drawQueue() {
    this.drawing = true;
    this.drawingEmitter.emit(true);
    this.element.nativeElement.getElementsByClassName('attack-modifier-draw')[0].classList.add('drawing');
    this.queueTimeout = setTimeout(
      () => {
        this.drawing = false;
        this.drawingEmitter.emit(false);
        this.queueTimeout = null;
        if (this.queue > 0) {
          this.queue--;
          this.current++;
          this.drawQueue();
        } else {
          this.element.nativeElement.getElementsByClassName('attack-modifier-draw')[0].classList.remove('drawing');
          if (this.queue < 0) {
            this.queue = 0;
          }
        }
        this.cdr.markForCheck();
      },
      settingsManager.settings.animations ? 2500 * settingsManager.settings.animationSpeed : 0
    );
  }
}
